import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Loader2,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  Zap,
  Upload,
  Link2,
  Lock,
  FileSpreadsheet,
  Download,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { timeAgo } from "@/lib/time";

const SAMPLE_CSV = `strain_name,strain_type,brand,category,unit,price,thc_pct,cbd_pct,stock_status,sku,image_url,order_url,description,weight_g
Blue Dream,hybrid,House,flower,3.5g,40,21.5,0.3,in_stock,BD-001,https://images.unsplash.com/photo-1503262028195-93c528f03218,https://shop.example.com/blue-dream,Sweet berry aroma with balanced uplift,3.5
Sour Diesel,sativa,Verano,flower,3.5g,45,24,0.1,in_stock,SD-002,,https://shop.example.com/sour-d,Fast-acting energizing sativa,3.5
Northern Lights,indica,Cresco,flower,1g,15,19,0.5,low_stock,NL-003,,,Classic relaxing indica,1
GG4 Live Resin,hybrid,Cookies,vape,1g,55,82,0,in_stock,GG-005,,,High-potency live resin cart,1
`;

const PROVIDER_TABS = [
  { id: "custom_csv", label: "CSV Upload", icon: Upload, status: "active" },
  { id: "custom_feed", label: "JSON Feed", icon: Link2, status: "active" },
  { id: "dutchie", label: "Dutchie POS", icon: Zap, status: "soon" },
  { id: "jane", label: "Jane", icon: Zap, status: "soon" },
];

export const IntegrationTab = () => {
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState("custom_csv");
  const [feedUrl, setFeedUrl] = useState("");
  const [syncEnabled, setSyncEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);

  const load = () =>
    api.get("/dispensary/integration").then((r) => {
      const integ = r.data.integration;
      setData(integ);
      setFeedUrl(integ.feed_url || "");
      setSyncEnabled(integ.sync_enabled ?? true);
      if (integ.provider && ["custom_csv", "custom_feed"].includes(integ.provider)) {
        setActiveTab(integ.provider);
      }
    });

  useEffect(() => {
    load();
  }, []);

  const downloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "budlink-sample-menu.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const onPickFile = () => fileInputRef.current?.click();

  const handleCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      toast.error("Please choose a .csv file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("CSV is larger than 5 MB");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    setUploadResult(null);
    try {
      const { data: r } = await api.post(
        "/dispensary/menu/csv-upload",
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setUploadResult(r);
      toast.success(`Imported ${r.products_upserted} products`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const saveFeed = async () => {
    if (!feedUrl.trim()) {
      toast.error("Add a JSON feed URL");
      return;
    }
    setSaving(true);
    try {
      await api.put("/dispensary/integration", {
        provider: "custom_feed",
        feed_url: feedUrl.trim(),
        sync_enabled: syncEnabled,
      });
      toast.success("Saved");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data: r } = await api.post("/dispensary/sync-now");
      const s = r.sync || {};
      if (s.ok) {
        toast.success(s.note || `${s.products_upserted || 0} products synced`);
      } else {
        toast.error(s.error || "Sync failed");
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSyncing(false);
    }
  };

  if (!data) return <div className="text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-6">
      <StatusStrip data={data} />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList
          className="bg-card border border-border h-auto p-1 gap-1 flex-wrap justify-start"
          data-testid="integration-tabs"
        >
          {PROVIDER_TABS.map(({ id, label, icon: Icon, status }) => (
            <TabsTrigger
              key={id}
              value={id}
              data-testid={`integration-tab-${id}`}
              className="px-4 py-2 gap-2 data-[state=active]:bg-foreground data-[state=active]:text-background"
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
              {status === "soon" && (
                <span className="text-[10px] uppercase tracking-wider bg-muted text-muted-foreground border border-border rounded-full px-1.5 py-0.5">
                  Soon
                </span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="custom_csv" className="mt-6">
          <div className="grid lg:grid-cols-[1fr_320px] gap-6 items-start">
            <div className="border border-border bg-card rounded-2xl p-6 space-y-5">
              <div>
                <div className="eyebrow mb-1 flex items-center gap-2">
                  <FileSpreadsheet className="w-3.5 h-3.5 text-accent" />
                  CSV Upload
                  <span className="pill bg-emerald-500/15 text-emerald-300 border-emerald-500/40 text-[10px]">
                    Free · No credentials
                  </span>
                </div>
                <h3 className="font-serif text-2xl">Upload your menu as a CSV</h3>
                <p className="text-sm text-foreground/70 mt-1">
                  Export from your POS, fix headers to match our template, and drop the file
                  below. Each upload replaces your live menu. 5 MB max.
                </p>
              </div>

              <div className="border-2 border-dashed border-border rounded-2xl p-8 text-center bg-background/40">
                <Upload className="w-8 h-8 mx-auto text-foreground/40 mb-3" />
                <div className="text-sm text-foreground/80 mb-1">
                  Drag &amp; drop coming soon — for now, click below
                </div>
                <div className="text-xs text-muted-foreground mb-4">
                  Accepted: .csv up to 5 MB
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={handleCsvUpload}
                  data-testid="integration-csv-file"
                />
                <div className="flex flex-wrap gap-3 justify-center">
                  <Button
                    type="button"
                    onClick={onPickFile}
                    disabled={uploading}
                    data-testid="integration-csv-pick"
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-6"
                  >
                    {uploading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    Choose CSV
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={downloadSample}
                    data-testid="integration-csv-sample"
                    className="rounded-full bg-transparent border-border h-10 px-6"
                  >
                    <Download className="w-4 h-4 mr-2" /> Sample CSV
                  </Button>
                </div>
              </div>

              {uploadResult && (
                <div
                  className="border border-border rounded-xl p-4 bg-background/40 text-sm space-y-2"
                  data-testid="integration-csv-result"
                >
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    <strong>{uploadResult.products_upserted}</strong> products imported
                    {uploadResult.errors?.length > 0 && (
                      <span className="text-muted-foreground">
                        · {uploadResult.errors.length} rows skipped
                      </span>
                    )}
                  </div>
                  {uploadResult.errors?.length > 0 && (
                    <ul className="text-xs text-rose-300/80 list-disc list-inside max-h-32 overflow-y-auto">
                      {uploadResult.errors.map((e, i) => (
                        <li key={i}>{e}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>

            <SidebarGuide
              title="CSV column reference"
              steps={[
                <>
                  Required: <code>strain_name</code>
                </>,
                <>
                  Recommended: <code>strain_type</code> (sativa / indica / hybrid),{" "}
                  <code>category</code>, <code>brand</code>, <code>price</code>,{" "}
                  <code>thc_pct</code>
                </>,
                <>
                  Optional: <code>cbd_pct</code>, <code>stock_status</code>,{" "}
                  <code>sku</code>, <code>unit</code>, <code>weight_g</code>,{" "}
                  <code>image_url</code>, <code>order_url</code>, <code>description</code>
                </>,
                <>Re-upload anytime — the new file fully replaces your menu.</>,
              ]}
            />
          </div>
        </TabsContent>

        <TabsContent value="custom_feed" className="mt-6">
          <div className="grid lg:grid-cols-[1fr_320px] gap-6 items-start">
            <div className="border border-border bg-card rounded-2xl p-6 space-y-5">
              <div>
                <div className="eyebrow mb-1 flex items-center gap-2">
                  <Link2 className="w-3.5 h-3.5 text-accent" />
                  JSON Feed
                  <span className="pill bg-emerald-500/15 text-emerald-300 border-emerald-500/40 text-[10px]">
                    Free · No credentials
                  </span>
                </div>
                <h3 className="font-serif text-2xl">Publish a live JSON menu URL</h3>
                <p className="text-sm text-foreground/70 mt-1">
                  Bud Link fetches your endpoint on demand and on a schedule. The feed
                  should return an array of products (same columns as the CSV).
                </p>
              </div>

              <Field label="Feed URL">
                <Input
                  data-testid="integration-feed-url"
                  type="url"
                  value={feedUrl}
                  onChange={(e) => setFeedUrl(e.target.value)}
                  placeholder="https://your-site.com/budlink-menu.json"
                  className="h-11 font-mono text-sm"
                />
              </Field>

              <div className="flex items-center justify-between gap-4 py-2 border-y border-border">
                <div>
                  <div className="text-sm font-medium">Auto-sync</div>
                  <div className="text-xs text-muted-foreground">
                    Bud Link re-fetches every 20 min
                  </div>
                </div>
                <Switch
                  data-testid="integration-feed-sync-enabled"
                  checked={syncEnabled}
                  onCheckedChange={setSyncEnabled}
                />
              </div>

              <div className="flex flex-wrap gap-3">
                <Button
                  type="button"
                  onClick={saveFeed}
                  disabled={saving}
                  data-testid="integration-feed-save"
                  className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 h-11 px-6"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    "Save feed URL"
                  )}
                </Button>
                <Button
                  type="button"
                  onClick={syncNow}
                  disabled={syncing || !feedUrl.trim()}
                  variant="outline"
                  data-testid="integration-feed-sync-now"
                  className="rounded-full bg-transparent border-border h-11 px-6"
                >
                  {syncing ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Sync now
                </Button>
              </div>
            </div>

            <SidebarGuide
              title="Expected response shape"
              steps={[
                <>
                  Return either a JSON array, or an object with{" "}
                  <code>products</code> or <code>items</code> key.
                </>,
                <>
                  Each product row uses the same fields as the CSV (strain_name,
                  price, thc_pct, etc.).
                </>,
                <>Must respond within 20 seconds and follow redirects.</>,
                <>
                  Sunnyside &amp; other Cresco brands can publish here while we
                  wait on partner API access.
                </>,
              ]}
            />
          </div>
        </TabsContent>

        <TabsContent value="dutchie" className="mt-6">
          <ComingSoonPanel
            provider="Dutchie POS"
            description="Direct partner API for Dutchie-powered dispensaries. We sync your live inventory every 20 min and via webhook."
            requirement="Requires partner credentials from Dutchie support."
            href="https://support.dutchie.com/hc/en-us/articles/27660267271187"
          />
        </TabsContent>

        <TabsContent value="jane" className="mt-6">
          <ComingSoonPanel
            provider="Jane Technologies"
            description="Marketplace integration for Jane-listed menus. Coming once partner credentials are issued."
            requirement="Requires partner agreement and API key from Jane."
            href="https://www.iheartjane.com/business"
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

const StatusStrip = ({ data }) => {
  const live = data.last_sync_status === "ok";
  return (
    <div
      className="border border-border bg-card rounded-2xl px-5 py-4 flex flex-wrap items-center gap-4 justify-between"
      data-testid="integration-status"
    >
      <div className="flex items-center gap-3">
        <div
          className={`w-9 h-9 rounded-full flex items-center justify-center ${
            live ? "bg-emerald-500/15 text-emerald-300" : "bg-muted text-muted-foreground"
          }`}
        >
          {live ? (
            <CheckCircle2 className="w-4 h-4" />
          ) : (
            <AlertCircle className="w-4 h-4" />
          )}
        </div>
        <div>
          <div className="text-sm font-medium">
            {data.provider
              ? `Provider · ${labelFor(data.provider)}`
              : "No provider configured"}
          </div>
          <div className="text-xs text-muted-foreground">
            {data.last_synced_at
              ? `Last sync ${timeAgo(data.last_synced_at)} · ${
                  data.last_sync_note || "no details"
                }`
              : "Not synced yet"}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        <div>
          <span className="text-foreground font-mono text-base">
            {data.products_count ?? 0}
          </span>{" "}
          products live
        </div>
      </div>
    </div>
  );
};

const ComingSoonPanel = ({ provider, description, requirement, href }) => (
  <div className="border border-border bg-card rounded-2xl p-8 text-center space-y-4">
    <div className="w-12 h-12 mx-auto rounded-full bg-muted flex items-center justify-center">
      <Lock className="w-5 h-5 text-muted-foreground" />
    </div>
    <div>
      <div className="eyebrow mb-1">Coming soon</div>
      <h3 className="font-serif text-2xl">{provider}</h3>
    </div>
    <p className="text-sm text-foreground/70 max-w-md mx-auto">{description}</p>
    <p className="text-xs text-muted-foreground">{requirement}</p>
    {href && (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center text-xs text-primary hover:underline"
      >
        Partner docs <ExternalLink className="w-3 h-3 ml-1" />
      </a>
    )}
    <div className="text-xs text-muted-foreground pt-2 border-t border-border">
      In the meantime, use the <strong>CSV Upload</strong> or{" "}
      <strong>JSON Feed</strong> tab — both are free and live now.
    </div>
  </div>
);

const SidebarGuide = ({ title, steps }) => (
  <aside className="border border-border bg-card rounded-2xl p-6 text-sm space-y-4">
    <div className="eyebrow">{title}</div>
    <ol className="space-y-3">
      {steps.map((s, i) => (
        <li key={i} className="flex items-start gap-2.5">
          <span className="mt-0.5 w-5 h-5 rounded-full bg-foreground text-background text-[10px] inline-flex items-center justify-center shrink-0">
            {i + 1}
          </span>
          <span className="leading-relaxed">{s}</span>
        </li>
      ))}
    </ol>
  </aside>
);

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
      {label}
    </Label>
    <div className="mt-1.5">{children}</div>
  </div>
);

const labelFor = (p) =>
  ({
    custom_csv: "CSV Upload",
    custom_feed: "JSON Feed",
    dutchie: "Dutchie POS",
    jane: "Jane",
  }[p] || p);
